import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { onErrorHandler, onGetCustomerSuccessHandler } from './handlers';
import Customer from '../models/customer';
import { delete_cookie, get, remove } from '../utils/cookies';
import { Page } from '../enums/page';
import { setShippingPrefill, stopLoading } from './application';
import { FunctionalityTrackingGaEvent } from '../utils/analytics/google';
import { GA_EVENT_TYPE } from '../components/shared/sections/my-account/utils';
import { config } from "../utils/config";
import { processOrderDetails } from "../utils/orders";
import i18n from "../utils/translations/i18n";
import { deleteAuthfrontCookiesAndRedirectToHome, setAuthorizationHeader } from '../utils/custom';

const { t } = i18n;

declare const window: any;

let shouldRedirectToHomePage = function () {
    var url = window.location.href;
    var currentPage = url.split('/').pop();
    if (currentPage) {
        //redirect user to home page if user logs in or logs out from order confirmation page
        if (currentPage.indexOf(Page.ORDERCONFIRMATION) > -1 || currentPage.indexOf(Page.CHECKOUTSHIPPING) <= -1) {
            return true;
        }
    }
    return false;
};

let getBaseUrl = function () {
    return window.locale_details.site_baseurl;
};

export const signoutAndRedirectToLogin = function (errorCode: string, email: string, returnToMyAccount: boolean = false, currentPage: Page = Page.INITIAL ) {
    const state = encodeURIComponent(btoa(JSON.stringify({
        clientId: window.Config?.MCP_AUTHFRONT_CLIENT_ID,
        redirectURL: returnToMyAccount && ( Page.MYACCOUNT === currentPage || Page.MYORDERS === currentPage || Page.ORDERDETAILS === currentPage ) ? window.location.href : getBaseUrl()
    })))
    const additionalParams = encodeURIComponent(btoa(JSON.stringify({
        screenType: 'SignIn'
    })))

    const logoutRedirectUrl = `${window.location.origin}/auth-callback?error=${errorCode}&state=${state}&additional_params=${additionalParams}&ui_locales=${config('locale')}&login_hint=${encodeURIComponent(email) || ''}`;
    window.mcpAuth.logout(logoutRedirectUrl);
}

export const customerApi = createApi({
    reducerPath: 'customerApi',
    // Custom baseQuery implementation that handles API requests and session expiration
    baseQuery: async (args, api, extraOptions) => {
        const baseQuery = fetchBaseQuery({
            baseUrl: '',
            prepareHeaders: (headers) => {
                setAuthorizationHeader(headers);
                headers.set(
                    window.Config?.NP_API_KEY_HEADER_NAME ?? "x-api-key",
                    window.Config?.NP_API_KEY_CLIENT_JS ?? "NSjMi38PBJ6s1rvNMA9FKO6FRn2iDd95bEDoaij8"
                );
                return headers;
            },
        });

        const result : any = await baseQuery(args, api, extraOptions);
        if (result.error?.status === 403 && result.error?.data?.message === window.MCP_AUTH_CONSTANT.FORBIDDEN_ERROR_MESSAGE) {
            if(window.authfrontSDK.isSignedIn()){
                const email = sessionStorage.getItem('np.emailAddress') || '';
                const state : any = api.getState();
                const currentPage = state.applicationState.currentPage;
                signoutAndRedirectToLogin('INVALID_SESSION',email, true, currentPage);
            }else{
                deleteAuthfrontCookiesAndRedirectToHome();
            }
        }

        return result;
    },
    tagTypes: ['customer'],
    endpoints: (builder) => ({
        getCustomerById: builder.query({
            query: ({ customerId }) => ({
                url: `${window.Config?.LAMBDA_CUSTOMER ?? 'https://npr-customer.api.pens.com/'}v1/customer/${customerId}`,
                method: 'GET'
            }),
            providesTags:['customer'],
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try{
                    const { data } = await queryFulfilled;
                    if(data.addresses && data.addresses.length > 0){
                        dispatch(setShippingPrefill(true));
                    }
                    else dispatch(setShippingPrefill(false));
                    dispatch(onGetCustomerSuccessHandler(data));
                }catch(error){
                    dispatch(onErrorHandler({operation: 'getCustomerById', err: JSON.stringify(error), errMsg: 'Error: while getting customer by Id', api: `${window.Config.LAMBDA_CUSTOMER}v1/customer/:customerId`}));
                }
            },
            transformResponse : (response: any ) => new Customer(response)
            
        }),
        getCustomerByEmail: builder.query({
            query: ({ ...params }) => ({
                url: `${window.Config?.LAMBDA_GET_CUSTOMER ?? "https://npr-get-customer-list.api.pens.com"}`,
                method: 'GET',
                params: params
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    return data;
                }
                catch (error) {
                    dispatch(onErrorHandler({operation: 'getCustomerByEmail', err: JSON.stringify(error), errMsg: 'Error: while getting customer by email', api: `${window.Config.LAMBDA_GET_CUSTOMER}`}));
                }
            }
        }),
        createGuestCustomer: builder.mutation({
            query: ({ ...data }) => ({
                url: `${window.Config?.LAMBDA_CREATE_GUEST_CUSTOMER ?? "https://npr-create-guest-customer.api.pens.com" }/v2`,
                method: 'POST',
                body: data
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    return data;
                }
                catch (error:any) {
                    if(error.error.status === 400 && error.error.data.message[0]?.code === 'DuplicateField') {
                        return true;
                    }
                    else {
                        dispatch(onErrorHandler({operation: 'createGuestCustomer', err: JSON.stringify(error), errMsg: 'Error: while creating guest customer', api: `${window.Config.LAMBDA_CREATE_GUEST_CUSTOMER}/v2`}));
                    }
                }
            }
        }),
        updateCustomer: builder.mutation({
            query: ({ ...data }) => ({
                url: `${window.Config?.LAMBDA_UPDATE_GUEST_CUSTOMER ?? "https://npr-update-guest-customer.api.pens.com" }`,
                method: 'POST',
                body: data
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    return data;
                }
                catch (error) {
                    dispatch(onErrorHandler({operation: 'updateCustomer', err: JSON.stringify(error), errMsg: 'Error: while updating customer', api: `${window.Config.LAMBDA_UPDATE_GUEST_CUSTOMER}`}));        
                }
            }
        }),

        updateCustomerInfo: builder.mutation({
            query: ({ customerId, customerVersion, updateFields }) => ({
                url: `${window.Config?.LAMBDA_CUSTOMER ?? 'https://npr-customer.api.pens.com/'}v1/updateCustomer/${customerId}`,
                method: 'PUT',
                body: {
                    version: Number(customerVersion) || 1,
                    updateFields
                }
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    return data;
                } catch(error) {
                    dispatch(onErrorHandler({operation: 'updateCustomerInfo', err: JSON.stringify(error), errMsg: 'Error: while updating customer information', api: `${window.Config?.LAMBDA_CUSTOMER}v1/updateCustomer/:customerId`}));
                }
            }
        }),
        
        // Todo: create a standalone function instead of mutation
        verifyAndLogout: builder.mutation({
            async queryFn({isChangePasswordFlow, email}, { dispatch, getState }, _extraOptions) {
                window.np.loader.show();
                FunctionalityTrackingGaEvent(GA_EVENT_TYPE.MY_ACCOUNT, 'signout');
                window?.OneTrust?.AllowAll();
                return { data: null };
            },
            async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
                try {
                        const { data } = await queryFulfilled;
                        window.np.sessionCleanUp();
                        window.reorder?.ls?.remove();
                        if (shouldRedirectToHomePage()) {
                            if(args.isChangePasswordFlow){
                                signoutAndRedirectToLogin('UPDATE_PASSWORD', args?.email);
                            }
                            else{
                                window.mcpAuth.logout(getBaseUrl());;
                            }
                        } else {
                            window.location.reload();
                        }
                    
                }  
                catch (error) {
                    console.log("=========Logout=error=========",error);
                    window.np.loader.hide();
                    dispatch(onErrorHandler({ operation: 'verifyTokenAndLogout', err: JSON.stringify(error), errMsg: 'Error: while verifying and logging out', api: null }));
                }
            }
        }),


        createCustomerAddress: builder.mutation({
            query: ({ ...data }) => ({
                url: `${window.Config?.LAMBDA_CUSTOMER ?? 'https://npr-customer.api.pens.com/'}v1/createAddress`,
                method: 'POST',
                body: data 
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    return data;
                } catch(error) {
                    dispatch(onErrorHandler({operation: 'createCustomerAddress', err: JSON.stringify(error), errMsg: 'Error: while creating customer address', api: `${window.Config?.LAMBDA_CUSTOMER}v1/createAddress`}));  
                    return error;
                }
            }
        }),

        setDefaultAddress: builder.mutation({
            query: ({ ...data }) => ({
                url: `${window.Config?.LAMBDA_CUSTOMER ?? 'https://npr-customer.api.pens.com/'}v1/setdefaultaddress`,
                method: 'POST',
                body: data 
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    return data;
                } catch(error) {
                    dispatch(onErrorHandler({operation: 'setDefaultAddress', err: JSON.stringify(error), errMsg: 'Error: while setting default address', api: `${window.Config?.LAMBDA_CUSTOMER}v1/setdefaultaddress`}));  
                    return error;
                }
            }
        }),

        editCustomerAddress: builder.mutation({
            query: ({ ...data }) => ({
                url: `${window.Config?.LAMBDA_CUSTOMER ?? 'https://npr-customer.api.pens.com/'}v1/editcustomeraddress`,
                method: 'POST',
                body: data
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    return data;
                } catch(error) {
                    dispatch(onErrorHandler({operation: 'editCustomerAddress', err: JSON.stringify(error), errMsg: 'Error: while editing customer address', api: `${window.Config?.LAMBDA_CUSTOMER}v1/editcustomeraddress`}));  
                    return error;
                }
            }
        }),

        deleteAddress: builder.mutation({
            query: ({customerId, addressType, addressId}) => ({
                url: `${window.Config?.LAMBDA_CUSTOMER ?? 'https://npr-customer.api.pens.com/'}v1/customer/${customerId}/deleteAddress/${addressType}/${addressId}`,
                method: 'DELETE'
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    return data;
                } catch(error) {
                    dispatch(onErrorHandler({operation: 'deleteAddress', err: JSON.stringify(error), errMsg: 'Error: while deleting the address', api: `${window.Config?.LAMBDA_CUSTOMER}v1/customer/:customerId/deleteAddress/:addressType/:addressId`}));
                    return error;
                }
            }
        }),

        getOrderDetails: builder.query({
            query: (data) => ({
                url: `${window.Config?.LAMBDA_CUSTOMER ?? 'https://npr-customer.api.pens.com/'}v1/getorderdetails?customerId=${data.customerId}&orderId=${data.orderId}`,
                method: 'GET'
            }),
            transformResponse(response: any) {
              return processOrderDetails(response, JSON.parse(config('statesData')), JSON.parse(config('statesKeyNameMap')));
            },
            async onQueryStarted(obj, { dispatch, getState, queryFulfilled }) {
                try{
                    await queryFulfilled;
                }
                catch(error){
                    console.log(error);
                } finally {
                    dispatch(stopLoading());
                }
            },
          }),
        
        getCustomerDataPreference: builder.query({
            query: ({ ...params }) => ({
                url: `${window.Config?.LAMBDA_CUSTOMER ?? 'https://npr-customer.api.pens.com/'}v1/getCustomerDataPreference`,
                method: 'GET',
                params: params
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    return data;
                }
                catch (error:any) {
                    console.log(error)
                    if(error?.error?.data?.data?.status === 404){
                        delete_cookie('np.rent');
                    }
                    dispatch(onErrorHandler({operation: 'getCustomerDataPreference', err: JSON.stringify(error), errMsg: 'Error: while getting customer data Preference', api: `${window.Config.LAMBDA_CUSTOMER}v1/getCustomerDataPreference`}));
                }
            }
        }) ,   
    }),
})

export const {
    useCreateGuestCustomerMutation, 
    useUpdateCustomerMutation,
    useGetCustomerByEmailQuery,
    useGetCustomerByIdQuery,
    useLazyGetCustomerByIdQuery,
    useUpdateCustomerInfoMutation,
    useVerifyAndLogoutMutation,
    useCreateCustomerAddressMutation,
    useSetDefaultAddressMutation,
    useEditCustomerAddressMutation,
    useDeleteAddressMutation,
    useGetOrderDetailsQuery,
    useGetCustomerDataPreferenceQuery,
} = customerApi;
